/**
 * Territory Canvas V1 — pure client domain model.
 *
 * WORLD-SPACE authority (Work Order §5): all element geometry is in absolute
 * Grid coordinates. Camera transforms are presentation-only and live in the
 * editor view (never mixed into persisted coordinates). This module is pure and
 * framework-free so the "easy to implement wrong" logic is unit-testable:
 * tier colours, footprint clipping (no auto-reveal), bounded Undo/Redo, z-order,
 * snapping, and element construction. NO backend writes happen here.
 */

export type ModuleType =
  | "TITLE" | "TAGLINE" | "DESCRIPTION" | "LINK" | "SOCIAL" | "CONTACTS"
  | "IMAGE" | "GIF" | "VIDEO" | "PDF" | "SHAPE" | "PIXEL_ART";

export type ShapeSubtype = "RECTANGLE" | "ROUNDED_RECTANGLE" | "ELLIPSE" | "LINE";
export type Tier = "STANDARD" | "PREMIUM" | "VIP_CORE" | "RESERVE" | "GRID_ZERO";

// ── SOCIAL module (revamp) — real brand icons + presets + layout controls ──
export interface SocialDestination {
  platform: string;      // logical brand token (mirrors backend SOCIAL_PLATFORMS)
  url: string;           // https target
  label?: string;        // optional per-link label override
  icon_color?: string;   // optional per-link icon colour token override
}
export type SocialPreset = "MINIMAL" | "BUTTONS" | "CARDS" | "ICONS_ONLY";
export interface SocialStyle {
  preset: SocialPreset;
  direction: "HORIZONTAL" | "VERTICAL" | "GRID";
  columns: number;
  gap: number;
  align: "START" | "CENTER" | "END";
  icon_size: number;
  font: string;
  font_size: number;
  icon_color: string;
  use_brand_colors: boolean;
  label_color: string;
  show_label: boolean;
  bg_enabled: boolean;
  bg_color: string;
  border_enabled: boolean;
  border_color: string;
  radius: "SQUARE" | "SLIGHT" | "ROUNDED" | "PILL";
  padding: number;
  hover: "NONE" | "LIFT" | "GLOW" | "SCALE";
}
export const DEFAULT_SOCIAL_STYLE: SocialStyle = {
  preset: "ICONS_ONLY", direction: "HORIZONTAL", columns: 3, gap: 14, align: "CENTER",
  icon_size: 30, font: "GRID_SANS", font_size: 14, icon_color: "GRID_WHITE",
  use_brand_colors: true, label_color: "GRID_WHITE", show_label: false,
  bg_enabled: false, bg_color: "GRID_GRAPHITE", border_enabled: false,
  border_color: "GRID_STEEL", radius: "ROUNDED", padding: 10, hover: "SCALE",
};
// Preset → seeded overrides applied when the user picks a preset in the inspector.
export const SOCIAL_PRESET_DEFAULTS: Record<SocialPreset, Partial<SocialStyle>> = {
  ICONS_ONLY: { show_label: false, bg_enabled: false, border_enabled: false, icon_size: 30, gap: 16, padding: 8, radius: "ROUNDED", hover: "SCALE", use_brand_colors: true },
  MINIMAL: { show_label: true, bg_enabled: false, border_enabled: false, icon_size: 22, gap: 18, padding: 6, radius: "SLIGHT", hover: "LIFT", use_brand_colors: false, font_size: 14 },
  BUTTONS: { show_label: true, bg_enabled: true, bg_color: "GRID_GRAPHITE", border_enabled: false, icon_size: 20, gap: 12, padding: 14, radius: "PILL", hover: "LIFT", use_brand_colors: false, font_size: 14 },
  CARDS: { show_label: true, bg_enabled: true, bg_color: "GRID_GRAPHITE", border_enabled: true, border_color: "GRID_STEEL", icon_size: 28, gap: 14, padding: 16, radius: "ROUNDED", hover: "LIFT", use_brand_colors: true, font_size: 13 },
};
export function socialStyleWithDefaults(raw?: Partial<SocialStyle> | Record<string, unknown> | null): SocialStyle {
  return { ...DEFAULT_SOCIAL_STYLE, ...((raw as Partial<SocialStyle>) ?? {}) };
}

// ── CONTACTS module (revamp) — typed rows + presets + display modes ──
export type ContactKind = "EMAIL" | "PHONE" | "WHATSAPP" | "WEBSITE" | "MAP";
export type ContactDisplay = "ICON" | "ICON_LABEL" | "ICON_DATA" | "BUTTON";
export interface ContactEntry {
  kind: ContactKind;
  value: string;
  label?: string;
  icon_color?: string;
  display?: ContactDisplay;
}
export type ContactsPreset = "ICONS_ONLY" | "BUSINESS" | "MINIMAL";
export interface ContactsStyle {
  preset: ContactsPreset;
  display: ContactDisplay;
  direction: "HORIZONTAL" | "VERTICAL" | "GRID";
  columns: number;
  gap: number;
  align: "START" | "CENTER" | "END";
  icon_size: number;
  font: string;
  font_size: number;
  icon_color: string;
  use_brand_colors: boolean;
  label_color: string;
  data_color: string;
  bg_enabled: boolean;
  bg_color: string;
  border_enabled: boolean;
  border_color: string;
  radius: "SQUARE" | "SLIGHT" | "ROUNDED" | "PILL";
  padding: number;
  hover: "NONE" | "LIFT" | "GLOW" | "SCALE";
}
export const DEFAULT_CONTACTS_STYLE: ContactsStyle = {
  preset: "MINIMAL", display: "ICON_LABEL", direction: "VERTICAL", columns: 2, gap: 12,
  align: "START", icon_size: 22, font: "GRID_SANS", font_size: 14, icon_color: "GRID_WHITE",
  use_brand_colors: false, label_color: "GRID_WHITE", data_color: "GRID_SILVER",
  bg_enabled: false, bg_color: "GRID_GRAPHITE", border_enabled: false, border_color: "GRID_STEEL",
  radius: "ROUNDED", padding: 10, hover: "LIFT",
};
export const CONTACTS_PRESET_DEFAULTS: Record<ContactsPreset, Partial<ContactsStyle>> = {
  ICONS_ONLY: { display: "ICON", direction: "HORIZONTAL", bg_enabled: false, border_enabled: false, icon_size: 28, gap: 16, padding: 8, radius: "ROUNDED", hover: "SCALE", use_brand_colors: true },
  BUSINESS: { display: "BUTTON", direction: "VERTICAL", bg_enabled: true, bg_color: "GRID_GRAPHITE", border_enabled: true, border_color: "GRID_STEEL", icon_size: 20, gap: 10, padding: 14, radius: "ROUNDED", hover: "LIFT", use_brand_colors: false, font_size: 14 },
  MINIMAL: { display: "ICON_LABEL", direction: "VERTICAL", bg_enabled: false, border_enabled: false, icon_size: 20, gap: 12, padding: 6, radius: "SLIGHT", hover: "LIFT", use_brand_colors: false, font_size: 14 },
};
export function contactsStyleWithDefaults(raw?: Partial<ContactsStyle> | Record<string, unknown> | null): ContactsStyle {
  return { ...DEFAULT_CONTACTS_STYLE, ...((raw as Partial<ContactsStyle>) ?? {}) };
}

// ── LINK module (revamp) — presets + icon + subtitle + full styling ──
export type LinkPreset = "TEXT" | "BUTTON" | "OUTLINE" | "CARD";
export type LinkIcon = "NONE" | "ARROW" | "FAVICON";
export interface LinkStyle {
  preset: LinkPreset;
  icon: LinkIcon;
  align: "START" | "CENTER" | "END";
  font: string;
  font_size: number;
  label_color: string;
  subtitle_color: string;
  underline: boolean;
  bg_enabled: boolean;
  bg_color: string;
  border_enabled: boolean;
  border_color: string;
  radius: "SQUARE" | "SLIGHT" | "ROUNDED" | "PILL";
  padding: number;
  hover: "NONE" | "LIFT" | "GLOW" | "SCALE";
}
export const DEFAULT_LINK_STYLE: LinkStyle = {
  preset: "TEXT", icon: "ARROW", align: "START", font: "GRID_SANS", font_size: 16,
  label_color: "GRID_ELECTRIC_BLUE", subtitle_color: "GRID_SILVER", underline: true,
  bg_enabled: false, bg_color: "GRID_ELECTRIC_BLUE", border_enabled: false, border_color: "GRID_ELECTRIC_BLUE",
  radius: "ROUNDED", padding: 12, hover: "LIFT",
};
export const LINK_PRESET_DEFAULTS: Record<LinkPreset, Partial<LinkStyle>> = {
  TEXT: { underline: true, bg_enabled: false, border_enabled: false, icon: "ARROW", label_color: "GRID_ELECTRIC_BLUE", padding: 4, radius: "SLIGHT", hover: "GLOW" },
  BUTTON: { underline: false, bg_enabled: true, bg_color: "GRID_ELECTRIC_BLUE", border_enabled: false, icon: "ARROW", label_color: "GRID_WHITE", padding: 14, radius: "PILL", hover: "LIFT" },
  OUTLINE: { underline: false, bg_enabled: false, border_enabled: true, border_color: "GRID_ELECTRIC_BLUE", icon: "ARROW", label_color: "GRID_ELECTRIC_BLUE", padding: 12, radius: "ROUNDED", hover: "GLOW" },
  CARD: { underline: false, bg_enabled: true, bg_color: "GRID_GRAPHITE", border_enabled: true, border_color: "GRID_STEEL", icon: "FAVICON", label_color: "GRID_WHITE", padding: 16, radius: "ROUNDED", hover: "LIFT" },
};
export function linkStyleWithDefaults(raw?: Partial<LinkStyle> | Record<string, unknown> | null): LinkStyle {
  return { ...DEFAULT_LINK_STYLE, ...((raw as Partial<LinkStyle>) ?? {}) };
}

// ── PIXEL ART module — W×H grid, 1 cell == 1 world/parcel unit ──
// cells: Uint16Array of palette indices; 0 = transparent, i>=1 → palette[i-1].
export const PIXEL_ART_MAX_DIM = 256;
export const PIXEL_ART_SIZE_PRESETS = [16, 32, 64, 128];
export interface PixelArt {
  w: number;
  h: number;
  palette: string[];      // hex colours (#rrggbb / #rrggbbaa); index 0 in cells = transparent
  rle: [number, number][]; // run-length [paletteIndexPlusTransparent, count]
}
export function decodeRle(rle: [number, number][], w: number, h: number): Uint16Array {
  const cells = new Uint16Array(w * h);
  let p = 0;
  for (const [idx, count] of rle) {
    for (let k = 0; k < count && p < cells.length; k++) cells[p++] = idx;
  }
  return cells;
}
export function encodeRle(cells: Uint16Array): [number, number][] {
  const out: [number, number][] = [];
  if (cells.length === 0) return out;
  let cur = cells[0], count = 1;
  for (let i = 1; i < cells.length; i++) {
    if (cells[i] === cur) count++;
    else { out.push([cur, count]); cur = cells[i]; count = 1; }
  }
  out.push([cur, count]);
  return out;
}

export interface Box { x: number; y: number; w: number; h: number }
export interface Line { x1: number; y1: number; x2: number; y2: number }

export interface CanvasElement {
  element_id: string;
  type: ModuleType;
  shape_subtype?: ShapeSubtype;
  box?: Box;
  line?: Line;
  rotation?: number;
  z: number;
  opacity: number;
  hidden: boolean;
  locked: boolean;
  glow: "OFF" | "LOW" | "MEDIUM" | "HIGH";
  glow_color?: string;
  footprint_fp?: string;
  // type-specific (loosely typed for the editor; canonicalized by the backend)
  text?: string;
  text_style?: Record<string, unknown>;
  label?: string;
  url?: string;
  subtitle?: string;
  link_style?: LinkStyle;
  pixel_art?: PixelArt;
  pdf_bg_asset_id?: string;
  destinations?: SocialDestination[];
  social_style?: SocialStyle;
  entries?: ContactEntry[];
  contacts_style?: ContactsStyle;
  asset_id?: string;
  present?: Record<string, number | string>;
  title?: string;
  fill_color?: string;
  line_color?: string;
  color?: string;
  radius?: string;
  autoplay?: boolean;
}

export interface ParcelRect { id: number; x: number; y: number; w: number; h: number; tier: Tier }
export interface CanvasStyle {
  background_mode: "NEUTRAL" | "SOLID_COLOR";
  background_color?: string;
  perimeter_color: string;
  perimeter_glow: "OFF" | "LOW" | "MEDIUM" | "HIGH";
}
export interface CanvasAuthority { identity_revision: number; membership_fingerprint: string }
export interface TerritoryGeometry {
  parcels: ParcelRect[];
  bounds: { min_x: number; min_y: number; max_x: number; max_y: number } | null;
  composition: Record<string, number>;
  primary: string | null;
  member_parcel_ids: number[];
}
export interface CanvasDoc {
  identity_id: string;
  revision: number;
  elements: CanvasElement[];
  style: CanvasStyle;
  footprint_epochs: Record<string, { parcel_ids: number[]; as_of_block: number }>;
  authority: CanvasAuthority;
  territory?: TerritoryGeometry;
}

// ── Canonical tier border colours (Work Order §51). System-owned; the user can
// NEVER override these. GRID_ZERO has a unique system treatment (§52).
export const TIER_COLORS: Record<Tier, string> = {
  STANDARD: "#c8d0dc",
  PREMIUM: "#2f7bff",
  VIP_CORE: "#f6c343",
  RESERVE: "#ff5a4d",
  GRID_ZERO: "#8b5cf6",
};
export const TIER_LABELS: Record<Tier, string> = {
  STANDARD: "Standard", PREMIUM: "Premium", VIP_CORE: "VIP Core",
  RESERVE: "Reserve", GRID_ZERO: "GRID ZERO",
};

// Approved colour tokens → concrete Grid palette (presentation-only mapping).
export const COLOR_HEX: Record<string, string> = {
  GRID_WHITE: "#f5f7fa", GRID_MIST: "#dfe6ef", GRID_SILVER: "#c8d0dc", GRID_STEEL: "#8a97a8",
  GRID_SLATE: "#556071", GRID_GRAPHITE: "#2b3340", GRID_OBSIDIAN: "#0d1117", GRID_BLACK: "#05070b",
  GRID_CYAN: "#22d3ee", GRID_AZURE: "#38bdf8", GRID_ELECTRIC_BLUE: "#2f7bff", GRID_INDIGO: "#4f46e5",
  GRID_TEAL: "#14b8a6", GRID_GOLD: "#f6c343", GRID_AMBER: "#f59e0b", GRID_CORAL: "#ff7a5c",
  GRID_RED: "#ff5a4d", GRID_MAGENTA: "#ec4899", GRID_VIOLET: "#8b5cf6", GRID_LIME: "#a3e635",
  GRID_GREEN: "#22c55e", GRID_EMERALD: "#10b981", GRID_ROSE: "#fb7185", GRID_SAND: "#d8c7a0",
  GRID_PLUM: "#6d28d9", GRID_ICE: "#bae6fd",
  // expanded palette
  GRID_CRIMSON: "#dc2626", GRID_ORANGE: "#f97316", GRID_YELLOW: "#facc15", GRID_CHARTREUSE: "#84cc16",
  GRID_MINT: "#4ade80", GRID_AQUA: "#2dd4bf", GRID_SKY: "#0ea5e9", GRID_COBALT: "#2563eb",
  GRID_PERIWINKLE: "#818cf8", GRID_LAVENDER: "#a78bfa", GRID_FUCHSIA: "#d946ef", GRID_PINK: "#f472b6",
  GRID_BROWN: "#92400e", GRID_TAN: "#c8a97e", GRID_OLIVE: "#708238", GRID_FOREST: "#166534",
  GRID_NAVY: "#1e3a8a", GRID_MAROON: "#7f1d1d", GRID_CHARCOAL: "#1f2937", GRID_PEARL: "#eef2f7",
};
export function hexOf(token: string | undefined, fallback = "#8a97a8"): string {
  return (token && COLOR_HEX[token]) || fallback;
}

export const FONT_STACKS: Record<string, string> = {
  GRID_SANS: "'Inter', system-ui, sans-serif",
  GRID_GEOMETRIC: "'Space Grotesk', 'Inter', sans-serif",
  GRID_DISPLAY: "'Archivo Black', 'Space Grotesk', sans-serif",
  GRID_CONDENSED: "'Oswald', 'Arial Narrow', sans-serif",
  GRID_MONO: "'JetBrains Mono', ui-monospace, monospace",
  GRID_SERIF: "'Fraunces', Georgia, serif",
  GRID_SLAB: "'Roboto Slab', Georgia, serif",
  GRID_ROUNDED: "'Quicksand', 'Inter', sans-serif",
  GRID_HANDWRITE: "'Caveat', cursive",
  GRID_ELEGANT: "'Playfair Display', Georgia, serif",
  GRID_TECH: "'Chakra Petch', ui-monospace, sans-serif",
  GRID_MODERN_SERIF: "'Instrument Serif', Georgia, serif",
  GRID_BOLD: "'Montserrat', 'Inter', sans-serif",
  GRID_CLASSIC_SERIF: "'Lora', Georgia, serif",
};

export const MAX_ELEMENTS = 2000;
export const UNDO_LIMIT = 100;

let _idc = 0;
export function tempId(): string {
  _idc += 1;
  return `tmp_${Date.now().toString(36)}_${_idc}`;
}

// ── Geometry helpers (world-space) ──
export function elementBBox(el: CanvasElement): Box {
  if (el.line) {
    const { x1, y1, x2, y2 } = el.line;
    return { x: Math.min(x1, x2), y: Math.min(y1, y2), w: Math.abs(x2 - x1), h: Math.abs(y2 - y1) };
  }
  return el.box ?? { x: 0, y: 0, w: 0, h: 0 };
}

function rectIntersects(a: Box, b: Box): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

/** Parcels an element's footprint epoch is authorized over (Work Order §26). */
export function authorizedParcels(el: CanvasElement, doc: CanvasDoc): ParcelRect[] {
  const epoch = el.footprint_fp ? doc.footprint_epochs[el.footprint_fp] : undefined;
  const parcels = doc.territory?.parcels ?? [];
  if (!epoch) return parcels; // unsaved/new: authorized over current territory
  const allowed = new Set(epoch.parcel_ids);
  return parcels.filter((p) => allowed.has(p.id));
}

/**
 * Does the element have ANY visible intersection with its authorized surface?
 * (Work Order §24/§25/§26). New parcels acquired after the element's footprint
 * epoch are NOT in `authorizedParcels`, so an old oversized element does NOT
 * auto-reveal into newly acquired area.
 */
export function hasVisibleFootprint(el: CanvasElement, doc: CanvasDoc): boolean {
  const bbox = elementBBox(el);
  return authorizedParcels(el, doc).some((p) => rectIntersects(bbox, p));
}

/** Intersection with the CURRENT full territory (drag-end validity, §25). */
export function intersectsTerritory(bbox: Box, territory?: TerritoryGeometry): boolean {
  if (!territory) return true;
  return territory.parcels.some((p) => rectIntersects(bbox, p));
}

// ── z-order operations (Work Order §34) — pure, compacted 0..N-1 ──
export function normalizeZ(elements: CanvasElement[]): CanvasElement[] {
  return [...elements].sort((a, b) => a.z - b.z).map((el, i) => ({ ...el, z: i }));
}
export function reorder(elements: CanvasElement[], id: string, target: number): CanvasElement[] {
  const sorted = [...elements].sort((a, b) => a.z - b.z);
  const from = sorted.findIndex((e) => e.element_id === id);
  if (from < 0) return elements;
  const [moved] = sorted.splice(from, 1);
  const clamped = Math.max(0, Math.min(target, sorted.length));
  sorted.splice(clamped, 0, moved);
  return sorted.map((el, i) => ({ ...el, z: i }));
}
export function bringToFront(elements: CanvasElement[], id: string): CanvasElement[] {
  return reorder(elements, id, elements.length);
}
export function sendToBack(elements: CanvasElement[], id: string): CanvasElement[] {
  return reorder(elements, id, 0);
}
export function bringForward(elements: CanvasElement[], id: string): CanvasElement[] {
  const z = elements.find((e) => e.element_id === id)?.z ?? 0;
  return reorder(elements, id, z + 2);
}
export function sendBackward(elements: CanvasElement[], id: string): CanvasElement[] {
  const z = elements.find((e) => e.element_id === id)?.z ?? 0;
  return reorder(elements, id, Math.max(0, z - 1));
}

// ── Snapping (Work Order §29). Screen-space threshold in world units. ──
export function snapValue(v: number, targets: number[], threshold: number): number {
  let best = v;
  let bestD = threshold;
  for (const t of targets) {
    const d = Math.abs(v - t);
    if (d < bestD) { bestD = d; best = t; }
  }
  return best;
}

// ── Bounded local Undo/Redo (Work Order §38) — session-only, ≤ UNDO_LIMIT ──
export interface HistoryState { elements: CanvasElement[]; style: CanvasStyle }
export class History {
  private past: HistoryState[] = [];
  private future: HistoryState[] = [];
  constructor(private limit = UNDO_LIMIT) {}
  push(s: HistoryState): void {
    this.past.push(clone(s));
    if (this.past.length > this.limit) this.past.shift();
    this.future = [];
  }
  canUndo(): boolean { return this.past.length > 1; }
  canRedo(): boolean { return this.future.length > 0; }
  undo(current: HistoryState): HistoryState | null {
    void current;
    if (!this.canUndo()) return null;
    const cur = this.past.pop()!;
    this.future.push(clone(cur));
    return clone(this.past[this.past.length - 1]);
  }
  redo(): HistoryState | null {
    if (!this.canRedo()) return null;
    const next = this.future.pop()!;
    this.past.push(clone(next));
    return clone(next);
  }
  get depth(): number { return this.past.length; }
}

function clone<T>(v: T): T { return JSON.parse(JSON.stringify(v)); }

// ── Element factory (Work Order §61: valid placement near territory centre) ──
export function centerOfTerritory(t?: TerritoryGeometry): { x: number; y: number } {
  if (!t || !t.bounds || t.parcels.length === 0) return { x: 0, y: 0 };
  // Prefer a valid owned parcel centre over the bbox centre (may be a hole).
  const cx = (t.bounds.min_x + t.bounds.max_x) / 2;
  const cy = (t.bounds.min_y + t.bounds.max_y) / 2;
  const inside = t.parcels.some((p) => cx >= p.x && cx <= p.x + p.w && cy >= p.y && cy <= p.y + p.h);
  if (inside) return { x: cx, y: cy };
  const p = t.parcels[0];
  return { x: p.x + p.w / 2, y: p.y + p.h / 2 };
}

/** Is a world point inside an owned parcel of the territory? */
export function isInsideOwned(t: TerritoryGeometry | undefined, pt: { x: number; y: number }): boolean {
  if (!t) return true;
  return t.parcels.some((p) => pt.x >= p.x && pt.x <= p.x + p.w && pt.y >= p.y && pt.y <= p.y + p.h);
}

/**
 * Deterministic valid placement point (Work Order §61): the current viewport
 * centre if it lies on owned surface, otherwise the nearest owned parcel centre.
 * This is what makes "Add Module" land in the CURRENTLY VISIBLE area at any zoom.
 */
export function placementPoint(t: TerritoryGeometry | undefined, viewportCenter?: { x: number; y: number }): { x: number; y: number } {
  if (viewportCenter && isInsideOwned(t, viewportCenter)) return viewportCenter;
  if (t && viewportCenter && t.parcels.length > 0) {
    let best = t.parcels[0]; let bestD = Infinity;
    for (const p of t.parcels) {
      const cxp = p.x + p.w / 2, cyp = p.y + p.h / 2;
      const d = (cxp - viewportCenter.x) ** 2 + (cyp - viewportCenter.y) ** 2;
      if (d < bestD) { bestD = d; best = p; }
    }
    return { x: best.x + best.w / 2, y: best.y + best.h / 2 };
  }
  return centerOfTerritory(t);
}

export function makeElement(type: ModuleType, t: TerritoryGeometry | undefined, over: Partial<CanvasElement> = {}, subtype?: ShapeSubtype, at?: { x: number; y: number }): CanvasElement {
  const c = placementPoint(t, at);
  const base: CanvasElement = {
    element_id: tempId(), type, z: 0, opacity: 1, hidden: false, locked: false, glow: "OFF",
    box: { x: c.x - 20, y: c.y - 8, w: 40, h: 16 },
    rotation: 0,
  };
  if (type === "SHAPE") {
    base.shape_subtype = subtype ?? "RECTANGLE";
    if (subtype === "LINE") {
      delete base.box; delete base.rotation;
      base.line = { x1: c.x - 20, y1: c.y, x2: c.x + 20, y2: c.y };
      base.line_color = "GRID_WHITE";
    } else {
      base.fill_color = "GRID_STEEL"; base.radius = "SQUARE";
    }
  } else if (type === "TITLE" || type === "TAGLINE" || type === "DESCRIPTION") {
    base.text = type === "TITLE" ? "Title" : type === "TAGLINE" ? "Tagline" : "Description";
    base.text_style = { font: "GRID_DISPLAY", font_size: type === "TITLE" ? 32 : 18, color: "GRID_WHITE", align: "LEFT" };
    base.radius = "SQUARE";
  } else if (type === "LINK") {
    base.box = { x: c.x - 40, y: c.y - 12, w: 80, h: 24 };
    base.label = "Open link"; base.url = ""; base.subtitle = "";
    base.color = "GRID_ELECTRIC_BLUE"; base.radius = "ROUNDED";
    base.link_style = { ...DEFAULT_LINK_STYLE };
  } else if (type === "SOCIAL") {
    base.box = { x: c.x - 45, y: c.y - 14, w: 90, h: 28 };
    base.destinations = [
      { platform: "x", url: "https://x.com/" },
      { platform: "instagram", url: "https://instagram.com/" },
      { platform: "linkedin", url: "https://linkedin.com/in/" },
    ];
    base.social_style = { ...DEFAULT_SOCIAL_STYLE };
    base.radius = "ROUNDED";
  } else if (type === "CONTACTS") {
    base.box = { x: c.x - 45, y: c.y - 20, w: 90, h: 40 };
    base.entries = [
      { kind: "EMAIL", value: "hello@example.com" },
      { kind: "PHONE", value: "+1 555 000 1234" },
      { kind: "WEBSITE", value: "https://example.com" },
    ];
    base.contacts_style = { ...DEFAULT_CONTACTS_STYLE };
    base.radius = "ROUNDED";
  } else if (type === "IMAGE" || type === "GIF" || type === "VIDEO") {
    base.box = { x: c.x - 40, y: c.y - 30, w: 80, h: 60 };
    base.present = { fit: "COVER", focal_x: 0.5, focal_y: 0.5, offset_x: 0, offset_y: 0, zoom: 1 };
    base.radius = "SQUARE"; if (type === "VIDEO") base.autoplay = false;
  } else if (type === "PDF") {
    base.box = { x: c.x - 28, y: c.y - 36, w: 56, h: 72 };
    base.title = ""; base.radius = "ROUNDED";
  } else if (type === "PIXEL_ART") {
    const dim = (over as { pixel_art?: { w?: number; h?: number } })?.pixel_art?.w ?? 32;
    const w = Math.min(PIXEL_ART_MAX_DIM, Math.max(1, dim));
    const h = Math.min(PIXEL_ART_MAX_DIM, Math.max(1, (over as { pixel_art?: { h?: number } })?.pixel_art?.h ?? w));
    base.box = { x: Math.round(c.x - w / 2), y: Math.round(c.y - h / 2), w, h };
    base.pixel_art = { w, h, palette: ["#22d3ee", "#f5f7fa", "#ec4899", "#f6c343", "#22c55e", "#05070b"], rle: [[0, w * h]] };
    base.radius = "SQUARE";
  }
  return { ...base, ...over };
}
